/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Directive, ElementRef, AfterViewInit, Input } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusManager } from '../services/focus-manager';

/**
 * Move focus on the element that installed with this Directive.
 */
@Directive({
    selector: '[nvAutoFocus]',
    standalone: true
})
export class AutoFocusDirective implements AfterViewInit {
    /**
     * Specifies the desired focusing policy.
     *
     * `self` - focus on the host element
     *
     * `initial` - focus on the first child element marked with `cdkFocusInitial`. If none exists, focus on the first focusable child element.
     */
    @Input() autoFocusPolicy: 'self' | 'initial' = 'self';

    constructor(
        private element: ElementRef,
        private focusManager: FocusManager
    ) {}

    @Input('nvAutoFocus') get focusEnabled() {
        return this._focusEnabled;
    }
    set focusEnabled(value: any) {
        this._focusEnabled = coerceBooleanProperty(value);
    }
    private _focusEnabled = true;

    ngAfterViewInit() {
        this.refocus();
    }

    /** Re-applies focus to the host element using the configured policy. */
    public refocus(): void {
        if (!this.focusEnabled) {
            return;
        }
        if (this.autoFocusPolicy === 'initial') {
            setTimeout(() => this.focusManager.focusChild(this.element.nativeElement, 'initial'));
        } else {
            setTimeout(() => this.focusManager.focusViaLastOrigin(this.element.nativeElement));
        }
    }
}
